import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, ArrowRight, Lock, Flag, Users, Eye, MessageCircle, UserCheck } from 'lucide-react';
import useInView from '../hooks/useInView';

const SHIELD = "/images/home/safety-research-hero.png";
const RONI = "/images/home/roni-feature.png";

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView(0.1);
  return (
    <div ref={ref} className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-3 font-outfit">{children}</p>
);

const pillars = [
  {
    icon: ShieldCheck,
    title: "Safety-by-design architecture",
    desc: "Safety is built into product decisions before scale: age-aware features, safer profile defaults, friend-based communication, limited exposure, and child-centered interaction rules.",
  },
  {
    icon: Users,
    title: "Parent-connected accounts",
    desc: "Child profiles are designed to be connected to parent or legal guardian oversight, with parent-aware controls, consent flows, safety notices, and support channels.",
  },
  {
    icon: MessageCircle,
    title: "Safer messaging boundaries",
    desc: "Messaging is structured around permitted relationships and safety checks. The platform discourages unknown-contact pressure, secrecy language, external handles, and unsafe personal information sharing.",
  },
  {
    icon: Eye,
    title: "RONI safety companion",
    desc: "RONI supports children in real time by detecting unsafe wording, bullying, manipulation, grooming indicators, private information sharing, and risky interaction patterns.",
  },
  {
    icon: Flag,
    title: "Reporting and blocking tools",
    desc: "Children and parents can report content, profiles, messages, clubs, and behavior. Blocking tools help children stop unwanted contact quickly.",
  },
  {
    icon: UserCheck,
    title: "Human review for serious cases",
    desc: "High-risk reports and repeated unsafe patterns can be escalated to human review, parent involvement, account restrictions, and authority reporting where required or appropriate.",
  },
  {
    icon: Lock,
    title: "Privacy-conscious child profiles",
    desc: "Profiles are designed not to require a child’s real public name, precise location, school, phone number, external handle, address, or other unsafe identifying details.",
  },
  {
    icon: ShieldCheck,
    title: "No harmful social mechanics",
    desc: "ROMIND KIDS avoids adult-style dating, gambling mechanics, toxic popularity pressure, negative ranking culture, and manipulative monetization aimed at children.",
  },
];

const responseSteps = [
  "Detect risk signals through reports, RONI checks, moderation queues, and account behavior patterns.",
  "Prioritize urgent child safety risks, including grooming, exploitation, threats, self-harm signals, and unsafe external-contact attempts.",
  "Restrict risky content or account features while a serious case is reviewed.",
  "Escalate to human review, parent or guardian involvement, and competent authorities where required or appropriate.",
  "Record safety decisions in access-controlled logs to detect repeat abuse and prevent evasion.",
];

const ChildSafety = () => (
  <main className="bg-[#05050A] min-h-screen">
    <section className="relative pt-32 pb-20 overflow-hidden">
      <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-emerald-600/12 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-purple-600/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionLabel>Child Safety Overview</SectionLabel>
            <h1 className="font-outfit font-black text-5xl sm:text-6xl text-white mb-6 tracking-tight leading-[1.05]">
              Safety is not a feature.<br />
              <span className="gradient-text">It is the foundation.</span>
            </h1>
            <p className="text-white/60 text-base sm:text-lg leading-relaxed">
              ROMIND KIDS is designed as a safer creative ecosystem for children aged 9–14, with parent-connected accounts, RONI safety guidance, reporting and blocking tools, moderation workflows, and strict rules against grooming, exploitation, bullying, unsafe contact, and private information sharing.
            </p>
            <div className="mt-6 p-4 rounded-xl bg-white/[0.035] border border-white/10">
              <p className="text-white/50 text-xs leading-relaxed">
                This overview explains the product and policy measures ROMIND KIDS uses to protect children. Full legal details are available in the Child Safety & Moderation Policy, Privacy Policy, Report Handling Policy, and Data Retention & Deletion Policy.
              </p>
            </div>
          </div>
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-emerald-500/20 rounded-full blur-[80px]" />
              <img src={SHIELD} alt="Child safety shield" className="relative w-full max-w-xs animate-float-slow" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section data-testid="safety-pillars" className="py-16 bg-[#080714]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>Safety Measures</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl text-white mb-4 tracking-tight max-w-2xl">
            The main protection layers inside ROMIND KIDS.
          </h2>
          <p className="text-white/50 text-sm leading-relaxed max-w-3xl mb-8">
            ROMIND KIDS combines product design, child privacy rules, parent involvement, RONI-assisted detection, human review, and clear enforcement. The goal is not only to remove harmful content after damage happens, but to reduce the chances of unsafe interaction from the beginning.
          </p>
        </FadeIn>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <FadeIn key={p.title} delay={Math.min(i * 50, 300)}>
                <div data-testid={`safety-pillar-${i}`} className="glass-card p-6 flex gap-5 items-start hover:border-emerald-500/20 transition-all h-full">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/12 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <Icon size={17} className="text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-outfit font-bold text-white text-[15px] mb-1.5">{p.title}</h3>
                    <p className="text-white/50 text-sm leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </div>
    </section>

    <section data-testid="child-safety-roni" className="py-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="glass-card p-8 sm:p-12 border border-cyan-500/15">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <SectionLabel>RONI Safety Layer</SectionLabel>
              <h2 className="font-outfit font-bold text-3xl text-white mb-4 tracking-tight">RONI helps children slow down before harm spreads.</h2>
              <p className="text-white/55 text-sm leading-relaxed mb-6">
                RONI is the AI-assisted safety companion built into ROMIND KIDS. RONI can help identify unsafe wording, risky patterns, private information sharing, bullying, manipulation, grooming indicators, threats, or pressure to move outside the platform.
              </p>
              <div className="space-y-3">
                {[
                  "Guides children toward safer wording and calmer choices.",
                  "Can block or slow down risky messages before they are sent.",
                  "Supports human moderation for serious or repeated safety concerns.",
                  "Helps children recognize when something feels unsafe or too personal.",
                  "Works as a protective layer, not as a replacement for parents or human review.",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <div className="w-4 h-4 rounded-full bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                    </div>
                    <span className="text-white/55 text-xs">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 scale-125 bg-cyan-500/25 rounded-full blur-[90px]" />
                <img src={RONI} alt="RONI AI Safety Companion" className="relative w-64 sm:w-72 lg:w-80 animate-float" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="py-16 bg-[#080714]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>Response System</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl text-white mb-6 tracking-tight">How ROMIND KIDS responds to safety concerns.</h2>
          <div className="glass-card p-6 sm:p-8">
            <div className="space-y-4">
              {responseSteps.map((step, index) => (
                <div key={step} className="flex gap-4 items-start">
                  <div className="w-8 h-8 rounded-full bg-purple-500/12 border border-purple-400/20 flex items-center justify-center text-purple-200 text-xs font-bold flex-shrink-0">
                    {index + 1}
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed pt-1">{step}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-white/8 pt-6 mt-6">
              <p className="text-white/38 text-xs leading-relaxed">
                For detailed rules, see the <Link to="/child-safety-policy" data-testid="child-safety-policy-link" className="text-cyan-400 hover:text-cyan-300 transition-colors">Child Safety & Moderation Policy</Link>, <Link to="/community-guidelines" className="text-cyan-400 hover:text-cyan-300 transition-colors">Community Guidelines</Link>, <Link to="/report-handling" className="text-cyan-400 hover:text-cyan-300 transition-colors">Report Handling Policy</Link>, and <Link to="/data-retention" className="text-cyan-400 hover:text-cyan-300 transition-colors">Data Retention & Deletion Policy</Link>.
              </p>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>

    <section className="py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/parents" data-testid="child-safety-parents-cta" className="px-6 py-3 rounded-full font-semibold text-sm bg-purple-600/80 border border-purple-400/40 text-white hover:bg-purple-500 transition-all text-center">
          Parent Safety Overview
        </Link>
        <Link to="/contact" data-testid="child-safety-contact-cta" className="px-6 py-3 rounded-full font-semibold text-sm bg-white/5 border border-white/15 text-white hover:bg-white/10 transition-all text-center flex items-center justify-center gap-2">
          Safety Inquiry <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  </main>
);

export default ChildSafety;
